import { ICaremapNote } from '../model/NotesClient';
import { FilterState } from '../components/NotesFilterControl';

const htmlToText = (html: string): string => {
    return new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
};

const matchesSearch = (note: ICaremapNote, searchText: string): boolean => {
    if (!searchText.trim()) {
        return true;
    }
    const query = searchText.toLowerCase();

    if (note.title?.toLowerCase().includes(query)) {
        return true;
    }

    if (htmlToText(note.html).toLowerCase().includes(query)) {
        return true;
    }

    if (note.tags?.join(' ').toLowerCase().includes(query)) {
        return true;
    }

    const summary = note.otherAttributes?.recording?.summary;

    const shortSummary = summary?.shortSummary;
    if (shortSummary && shortSummary.toLowerCase().includes(query)) {
        return true;
    }

    const highLevelSummary = summary?.highLevelSummary;
    if (highLevelSummary && highLevelSummary.toLowerCase().includes(query)) {
        return true;
    }

    if (summary?.participants?.join(' ').toLowerCase().includes(query)) {
        return true;
    }

    const detailed = summary?.detailedSummary;
    if (detailed) {
        const detailedText = [
            ...(detailed.keyTopicsDiscussed || []),
            ...(detailed.decisionsMade || []),
            ...(detailed.instructionsOrRecommendations || []),
        ].join(' ').toLowerCase();
        if (detailedText.includes(query)) {
            return true;
        }
    }

    // Recording transcription
    const transcription = note.otherAttributes?.recording?.transcription;
    if (transcription && transcription.toLowerCase().includes(query)) {
        return true;
    }

    // Tasks (API stores as "tasks" on summary)
    const tasks = (summary as any)?.tasks;
    if (tasks) {
        const tasksText = tasks.map((t: any) =>
            [t.description, t.details].filter(Boolean).join(' ')
        ).join(' ').toLowerCase();
        if (tasksText.includes(query)) {
            return true;
        }
    }

    return false;
};

export const filterNotes = <TNote extends ICaremapNote>(notes: TNote[], filters: FilterState, searchText: string): TNote[] => {
    return notes.filter(note => {
        // Note Type filters (AND logic - note must have ALL selected types)
        if (filters.noteType.length > 0) {
            const hasRecording = Boolean(note.otherAttributes?.recording);
            const hasActionItems = Boolean((note.otherAttributes?.recording?.summary?.actionItems?.length ?? 0) > 0);
            const hasOtherAttachments = Boolean(note.attachments?.length > 0) && (!hasRecording || note.attachments?.length > 1);
            const matchesAllNoteTypes = filters.noteType.every(filterType => {
                switch (filterType) {
                    case 'hasRecording': return hasRecording;
                    case 'hasActionItems': return hasActionItems;
                    case 'hasOtherAttachments': return hasOtherAttachments;
                    default: return false;
                }
            });
            if (!matchesAllNoteTypes) {
                return false;
            }
        }

        // Care Category filters
        if (filters.careCategory.length > 0) {
            const noteSection = note.otherAttributes?.noteTarget?.section;
            if (!filters.careCategory.includes(noteSection)) {
                return false;
            }
        }

        // Tags filters (OR logic - note must have at least one selected tag)
        if (filters.tags.length > 0) {
            const noteTags = note.tags || [];
            const hasMatchingTag = filters.tags.some(filterTag => noteTags.includes(filterTag));
            if (!hasMatchingTag) {
                return false;
            }
        }

        // Search text filter
        if (!matchesSearch(note, searchText)) {
            return false;
        }

        return true;
    });
};
